import { useEffect } from 'react';

import { useAuthContext } from 'lib/core/context/AuthProvider';

import CONTACT_STATES from 'lib/common/constants/contactStates';

import TTask from 'lib/common/types/Task';

import destroyTask from '../utils/destroyTask';
import handleCallRefresh from '../utils/handleCallRefresh';
import handleConnectedContact from '../utils/handleConnectedContact';
import handleErrorPreviewDialingTask from '../utils/handleErrorPreviewDialingTask';
import handlePendingPreviewDialingTask from '../utils/handlePendingPreviewDialingTask';
import registerPauseTaskEvents from '../utils/registerPauseTaskEvents';
import useMissedContact from './useMissedContact';
import useMoveTaskToACW from './useMoveTaskToACW';

export default function useContactHandlers({ matchNewContact, handleContactChange, fetch_, config, getTasks }) {
  const { getUser } = useAuthContext();
  const moveTaskToACW = useMoveTaskToACW({ handleContactChange });
  const handleMissedContact = useMissedContact({ handleContactChange });

  useEffect(() => {
    const connect = (window as any).getConnect();

    const contactSubscription = connect.contact((contact: connect.Contact) => {
      contact.onConnecting(matchNewContact);

      contact.onIncoming(matchNewContact);

      contact.onAccepted(() => {
        handleContactChange({ contact, status: CONTACT_STATES.ACCEPTED, eventTimestamp: Date.now() });
      });

      contact.onPending(handlePendingPreviewDialingTask(handleContactChange));

      contact.onError(handleErrorPreviewDialingTask(handleContactChange));

      contact.onConnected(async (latestContact: connect.Contact) =>
        handleConnectedContact({ contact: latestContact, handleContactChange, fetch_, config, getUser })
      );

      /**
       * We're double handling ACW here, because we cannot differentiate ACW triggered by us vs. customer disconnecting.
       * If we just rely on us moving the task to acw, when the customer disconnects nothing will happen
       */
      contact.onACW((latestContact: connect.Contact) => moveTaskToACW(latestContact));

      contact.onMissed(handleMissedContact);

      /**
       * NEONNOW should have destroyed the contact already, before waiting for the connect event. Handle them here as well, just in case
       */
      contact.onDestroy((contact: connect.Contact) => destroyTask({ contact, handleContactChange }));

      contact.onRefresh((latestContact: connect.Contact) => {
        const existingTask = getTasks().find((existingTask: TTask) => existingTask.taskId === contact.contactId);

        // If task is in ACW, we bail out
        if (existingTask?.status === CONTACT_STATES.ACW) {
          return;
        }

        registerPauseTaskEvents(contact, getTasks, handleContactChange);

        handleCallRefresh({ handleContactChange, contact: latestContact, existingTask, moveTaskToACW });
      });
    });

    return () => contactSubscription.unsubscribe();
  }, []);
}
